import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Button, Paper } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClientLayout from '../../layouts/ClientLayout';
import BreadcrumbsShared from '../../components/Shared/BreadcrumbsShared';
import { GENERAL_ROUTE } from '../../routes/const';
import { getOneClientRequest } from '../../redux/actions/clientRequest/clientRequestActions';
import { formatDate } from '../../functions/formatters';
import { serviceCRUD, serviceScheduleCRUD } from '../../http/CRUD';
import Loader from '../../components/Shared/Loader';

const ClientRequestDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.clientRequest);
  const [requestData, setRequestData] = useState(null);
  const [localLoading, setLocalLoading] = useState(true);

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          setLocalLoading(true);
          const result = await dispatch(getOneClientRequest(id));
          if (result) {
            let enriched = { ...result };
            // Обогащаем спецификации данными об услугах и графиках
            if (result.specifications?.length) {
              enriched.specifications = await Promise.all(
                result.specifications.map(async (spec) => {
                  const enrichedSpec = { ...spec };
                  try {
                    if (spec.serviceId && !spec.service?.name) {
                      enrichedSpec.service = await serviceCRUD.get(spec.serviceId);
                    }
                    if (spec.scheduleId && !spec.schedule?.name) {
                      enrichedSpec.schedule = await serviceScheduleCRUD.get(spec.scheduleId);
                    }
                  } catch (e) {
                    console.error("Ошибка загрузки данных спецификации:", e);
                  }
                  return enrichedSpec;
                })
              );
            }
            setRequestData(enriched);
          }
        } catch (e) {
          console.error("Ошибка загрузки заявки:", e);
        } finally {
          setLocalLoading(false);
        }
      })();
    }
  }, [id, dispatch]);

  if (loading || localLoading) {
    return (
      <ClientLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Loader />
        </Box>
      </ClientLayout>
    );
  }

  if (!requestData || !requestData.id) {
    return (
      <ClientLayout>
        <BreadcrumbsShared
          breadcrumbs={[
            { text: 'Главная', link: GENERAL_ROUTE + '/' },
            { text: 'История заявок', link: GENERAL_ROUTE + '/history' },
            { text: 'Заявка не найдена', link: '#' },
          ]}
        />
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" color="error">
            Заявка не найдена
          </Typography>
        </Box>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <BreadcrumbsShared
        breadcrumbs={[
          { text: 'Главная', link: GENERAL_ROUTE + '/' },
          { text: 'История заявок', link: GENERAL_ROUTE + '/history' },
          { text: `Заявка #${requestData.number}`, link: '#' },
        ]}
      />

      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
          sx={{ mb: 3 }}
        >
          Назад
        </Button>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Заявка #{requestData.number}
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1">
              <strong>Дата создания:</strong> {formatDate(requestData.createdAt)}
            </Typography>
            <Typography variant="body1">
              <strong>Статус:</strong> {requestData.state?.name || 'Не указан'}
            </Typography>
            <Typography variant="body1">
              <strong>Общая сумма:</strong> {requestData.specificationsSummary} ₸
            </Typography>
            <Typography variant="body1">
              <strong>Количество услуг:</strong> {requestData.specifications?.length || 0}
            </Typography>
            {requestData.filial?.name && (
              <Typography variant="body1">
                <strong>Филиал:</strong> {requestData.filial.name}
              </Typography>
            )}
          </Box>

          {requestData.specifications && requestData.specifications.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Детали услуг
              </Typography>
              {requestData.specifications.map((spec, index) => (
                <Box key={spec.id || index} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                  <Typography variant="body1" fontWeight="bold">
                    {spec.service?.name || 'Услуга не указана'}
                  </Typography>
                  {spec.service?.code && (
                    <Typography variant="body2" color="text.secondary">
                      Код: {spec.service.code}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    Количество: {spec.count || 0}
                  </Typography>
                  <Typography variant="body2">
                    Цена за единицу: {spec.price} ₸
                  </Typography>
                  <Typography variant="body2">
                    График: {spec.schedule?.name || 'Не указан'}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    Сумма: {spec.summary} ₸
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      </Box>
    </ClientLayout>
  );
};

export default ClientRequestDetail;
